import {
  AppRootElementBlueprint,
  createFrontendPlugin,
} from '@backstage/frontend-plugin-api';
import {
  EntityCardBlueprint,
  EntityContentBlueprint,
} from '@backstage/plugin-catalog-react/alpha';
import type { Entity } from '@backstage/catalog-model';
import {
  DashboardWidgetBlueprint,
  MyWorkCardBlueprint,
  tabVisible,
} from '@platform/plugin-platform-react';
import { ArgocdHealthWatcher } from './ArgocdHealthWatcher';

/**
 * Le filtre reprend `isArgocdConfigured` (`entityShared.tsx`), qui vérifie les
 * annotations réellement portées par ce catalogue.
 *
 * NOTE conservée de l'original : `isArgocdAvailable()` de
 * `@roadiehq/backstage-plugin-argo-cd` vérifie ses propres annotations
 * (`argocd/app-name`, `argocd/app-selector`, `argocd/project-name`), dont
 * aucune n'est portée par les entités d'ici — annotées
 * `platform.io/argocd-app-prefix` à la place. Ce décalage faisait que la carte
 * ne s'affichait jamais.
 *
 * Devenu un filtre d'extension, il a une conséquence NOUVELLE : l'onglet
 * lui-même disparaît pour une entité sans annotation ArgoCD, là où l'ancien
 * `EntitySwitch` masquait seulement la carte à l'intérieur d'un onglet
 * « Livraison (CD) » partagé. C'est le comportement voulu par la spec — un
 * client sans ArgoCD n'a pas d'onglet ArgoCD — mais c'est un changement
 * visible, pas une simple extraction.
 */
const isArgocdConfigured = (entity: Entity): boolean =>
  Boolean(entity.metadata.annotations?.['platform.io/argocd-app-prefix']) ||
  Boolean(entity.metadata.annotations?.['argocd/app-name']);

const argocdContent = EntityContentBlueprint.make({
  name: 'deployments',
  params: {
    path: '/deploy-run',
    title: 'Déploiements',
    filter: tabVisible('deployments', isArgocdConfigured),
    loader: () =>
      import('./ArgocdDeploymentsCard').then(m => <m.ArgocdDeploymentsCard />),
  },
});

/**
 * Le tableau de bord d'un systeme : une ligne par composant, avec son etat de
 * synchronisation et de sante ArgoCD, sa revision et son image.
 *
 * Il occupait l'onglet de vue d'ensemble de `systemPage` (`EntityPage.tsx`).
 * Devenu une carte `content`, il se place au meme endroit — la vue d'ensemble
 * du catalogue natif — sans que la page d'entite ait besoin d'exister.
 */
const isSystem = (entity: Entity): boolean =>
  entity.kind?.toLowerCase() === 'system';

const projectDashboardCard = EntityCardBlueprint.make({
  name: 'project-dashboard',
  params: {
    type: 'content',
    filter: isSystem,
    loader: () =>
      import('./ProjectDashboardCard').then(m => <m.ProjectDashboardCard />),
  },
});

/**
 * « Mes services » sur la page « Mon travail » du noyau : ce qui est déployé,
 * par environnement. La carte interroge
 * `/api/argocd/deployments`, l'état ArgoCD des applications —
 * même source que les deux autres cartes de ce plugin.
 */
const myServicesCard = MyWorkCardBlueprint.make({
  name: 'my-services',
  params: {
    width: 'full',
    loader: () => import('./MyServicesCard').then(m => <m.MyServicesCard />),
  },
});

/**
 * Surveille l'état ArgoCD et publie des toasts (API du socle). Vivait dans
 * `PlatformNotifications`, dans `packages/app`.
 */
const healthWatcher = AppRootElementBlueprint.make({
  name: 'health-watcher',
  params: { element: <ArgocdHealthWatcher /> },
});

// Vue d'ensemble (maquette) : état ArgoCD des services du catalogue.
const applicationsWidget = DashboardWidgetBlueprint.make({
  name: 'applications',
  params: { area: 'main', loader: () => import('./dashboard/widgets').then(m => <m.ApplicationsWidget />) },
});
const environmentStateWidget = DashboardWidgetBlueprint.make({
  name: 'environment-state',
  params: { area: 'main', loader: () => import('./dashboard/widgets').then(m => <m.EnvironmentStateWidget />) },
});
const attentionWidget = DashboardWidgetBlueprint.make({
  name: 'attention',
  params: { area: 'side', loader: () => import('./dashboard/widgets').then(m => <m.AttentionWidget />) },
});

export default createFrontendPlugin({
  pluginId: 'argocd',
  extensions: [
    argocdContent,
    projectDashboardCard,
    myServicesCard,
    healthWatcher,
    applicationsWidget,
    environmentStateWidget,
    attentionWidget,
  ],
});
